import authentication from "@irati/auth"
import defaultAuth from "@irati/auth/helpers/defaultAuth"
import companyBenefitsService from "@irati/deals/services/companyBenefits"
import {query as ValueListQuery} from "@irati/companyvalues/apollo/query/CompanyValuesList"
import {mutation as ArchiveValueMutation} from "@irati/companyvalues/apollo/mutation/Archive"
import {CompanyValue} from "@irati/companyvalues/types"
import client from "@irati/utils/services/apollo"
import {Tenant, TenantOnboardingStep, TenantOnboardingStepStates, APIResponse, TeamState} from "@irati/types"
import {store} from "@irati/store"
import productSettingsService from "@irati/permissions/services/productSettings"
import AuthActions from "@irati/auth/store/actions/AuthActions"
import teamService from "@irati/identity/services/teams"
import brandingService from "@irati/brand/services/branding"
import {UpdateBrandingInput} from "@irati/types/ops"
import BrandingStream from "@irati/brand/streams/branding"
import {SSO_TYPE_SAML} from "@irati/sso/constants/signUpOptions"
import {deleteSaml} from "@irati/sso/services/sso"

import stepDetails, {sectionMapping, SetupSections, SetupSteps, Step} from "../constants/stepConfig"

export const isPathActive = (path: string): boolean => location.pathname.includes(path)

export const getStepDetails = (step: TenantOnboardingStep) => {
  return stepDetails[step.stepId] || {}
}

export const isValidStep = (step: TenantOnboardingStep) => {
  if (Object.keys(stepDetails).includes(step.stepId)) {
    if (stepDetails[step.stepId].hideStep) {
      return !stepDetails[step.stepId].hideStep()
    }
    return true
  }
}

export const generateReadableStepNumber = (stepNumber: number): string => (stepNumber >= 10 ? `${stepNumber}` : `0${stepNumber}`)

export const orderSteps = (steps: TenantOnboardingStep[]) =>
  steps.sort((stepA, stepB) => getStepDetails(stepA).order - getStepDetails(stepB).order)

export const getFirstIncompleteStep = (steps: TenantOnboardingStep[]) => {
  const firstUncompleteStep = orderSteps(steps).find((step: TenantOnboardingStep) => {
    const stepInfo = getStepDetails(step)
    return step.state !== TenantOnboardingStepStates.Done && (!stepInfo.hideStep || !stepInfo.hideStep())
  })
  return firstUncompleteStep || steps[steps.length - 1]
}
export const areAllStepsDone = (steps: TenantOnboardingStep[]) => !steps.some((step) => step.state !== TenantOnboardingStepStates.Done)

const getNextStep = (currentStepId: string, steps: TenantOnboardingStep[]) => {
  const orderedSteps = orderSteps(steps)
  const currentIndex = orderedSteps.findIndex((step: TenantOnboardingStep) => step.stepId === currentStepId)
  if (currentIndex === orderedSteps.length) return orderedSteps[orderedSteps.length - 1]

  const nextValidStep = orderedSteps!.find((step: TenantOnboardingStep, index: number) => {
    const stepInfo = getStepDetails(step)
    return (!stepInfo.hideStep || !stepInfo.hideStep()) && index > currentIndex
  })

  return nextValidStep || orderedSteps[orderedSteps.length - 1]
}

export const getNextStepPath = (currentStepId: string, steps: TenantOnboardingStep[]) =>
  getStepDetails(getNextStep(currentStepId, steps)).nestedPath

export const getOrderedSectionSteps = (steps: TenantOnboardingStep[], section: SetupSections) =>
  orderSteps(steps).filter((step) => sectionMapping[section].includes(step.stepId))

export const isDefaultBrand = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const {businessLogoUrl, primaryColour} = BrandingStream.get()
    const {imageLink: defaultImageLink, color: defaultColor} = defaultAuth.userTenant.settings
    resolve(businessLogoUrl === defaultImageLink && primaryColour === defaultColor)
  })
}

export const resetBrand = (): Promise<Tenant> => {
  const tenant = authentication.userTenant()
  const {imageLink: businessLogoUrl, color: primaryColour} = defaultAuth.userTenant.settings

  const data: UpdateBrandingInput = {
    businessId: tenant.uuid,
    teamId: tenant.rootTeamId,
    businessLogoUrl,
    primaryColour
  }

  return brandingService.update(data)
}

const benefitQueryOptions = {
  limit: 1000
}

export const isDefaultBenefits = (): Promise<boolean> =>
  companyBenefitsService.list(benefitQueryOptions).then((res: any) => res.total === 0)

export const resetBenefits = (): Promise<void> =>
  companyBenefitsService.list(benefitQueryOptions).then((res: any) => {
    const allArchivePromises = res.data.map((benefit: any) => companyBenefitsService.archive(benefit.uuid))
    return Promise.all(allArchivePromises)
  })

export const isDefaultPerksVisibility = async() => {
  const business = authentication.business()
  const ps = await productSettingsService.get(business.uuid, business.rootTeamId)
  const blacklistedTags = ps.perksSettings?.blacklistedTags || []
  const blacklistedDeals =  ps.perksSettings?.blacklistedPerks || []
  return !blacklistedTags.length && !blacklistedDeals.length
}

export const resetPerksVisibility = () => {
  const business = authentication.business()
  return productSettingsService.updatePerkSettings(business.uuid, business.rootTeamId, {
    blacklistedPerks: [],
    blacklistedTags: []
  })
}

export const isDefaultWelcomeEmail = () => {
  return new Promise<boolean>((resolve) => {
    const tenant = authentication.userTenant()
    resolve(tenant.settings.welcomeEmail === null)
  })
}

export const resetWelcomeEmail = () => {
  const tenant = authentication.userTenant()

  const data: UpdateBrandingInput = {
    businessId: tenant.uuid,
    teamId: tenant.rootTeamId,
    welcomeEmail: ""
  }

  return brandingService.update(data).then(() => {
    return store.dispatch(AuthActions.updateTenant(tenant))
  })
}

export const isReadyToInvite = (steps: TenantOnboardingStep[]) =>
  steps.every((step) => step.state === TenantOnboardingStepStates.Done || step.stepId === SetupSteps.INVITE)

export const isDefaultTeams = () => {
  const tenant = authentication.userTenant()
  // do we have 0 teams?
  return teamService.list(tenant.uuid, {state: TeamState.Generic, userTotalOnly: true}).then((teams) => !teams.length)
}

export const resetTeams = () => {
  const tenant = authentication.userTenant()
  return teamService.list(tenant.uuid, {state: TeamState.Generic, userTotalOnly: true}).then((teams) => {
    return Promise.all(
      teams.map((team) => {
        return teamService.delete(tenant.uuid, team.uuid)
      })
    )
  })
}

const getValues = () => {
  const tenant = authentication.userTenant()
  const variables = {
    tenantId: tenant.uuid,
    limit: 100
  }
  return client.query({query: ValueListQuery, variables, fetchPolicy: "network-only"}).then(({data}) => data.companyValues)
}

export const isDefaultValues = () => getValues().then(({count}) => count === 0)

export const resetValues = () => {
  const tenant = authentication.userTenant()
  const archivePromises: Promise<any>[] = []

  return getValues()
    .then(({results}) => {
      results.map((companyValue: CompanyValue) => {
        const variables = {
          tenantId: tenant.uuid,
          companyValue
        }
        archivePromises.push(client.mutate({mutation: ArchiveValueMutation, variables}))
      })
    })
    .then(() => Promise.all(archivePromises))
    .then(getValues)
}

export const isDefaultSSO = (isSSOStep: boolean): Promise<boolean> => {
  const tenant = authentication.userTenant()
  return new Promise((resolve) => {
    resolve(!isSSOStep || tenant.settings.signUpOption !== SSO_TYPE_SAML)
  })
}

export const resetSSO = (): Promise<APIResponse> => {
  return deleteSaml()
}

interface StepWithID extends Step {
  stepId: string
}

export const getCurrentTenantSetupStepConfig = (): StepWithID | null => {
  const urlArray = window.location.pathname.split("/")
  const path = urlArray[urlArray.length - 1]
  let currentStepConfig: StepWithID | null = null
  Object.keys(stepDetails).map((stepId) => {
    if (stepDetails[stepId].nestedPath === `/${path}`) {
      currentStepConfig = {...stepDetails[stepId], stepId}
    }
  })
  return currentStepConfig
}
